import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import IconButton from "@material-ui/core/IconButton";

import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import BlockIcon from "@material-ui/icons/Block";
import StorefrontIcon from "@material-ui/icons/Storefront";
import RefreshIcon from "@material-ui/icons/Refresh";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import ShopVerificationDetails from "./ShopVerificationDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import Field from "designV2/components/Field";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import { assetUrl, readableError } from "../shopFields";
import "../superShops.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 * ------------------------------------------------------------------ */

const SHOP_FILTERS = `
  name: $name
  shopAdminName: $shopAdminName
  verified: $verified
  isRejected: $isRejected
  rootCategory: $rootCategory
`;

const SHOP_VARIABLES = `
  $limit: Int
  $skip: Int
  $name: String
  $shopAdminName: String
  $verified: Boolean
  $isRejected: Boolean
  $rootCategory: ID
  $createdAtSort: Int
`;

const GET_SHOPS = gql`
  query getShopsByAdmin(${SHOP_VARIABLES}) {
    getShopsByAdmin(
      pagination: { limit: $limit, skip: $skip }
      filters: { ${SHOP_FILTERS} }
      sort: { createdAt: $createdAtSort }
    ) {
      _id
      name
      logoUrl
      bannerUrl
      address
      description
      phoneNumbers
      averageRate
      active
      state
      budget
      verified
      isRejected
      rejectionMessage
      createdAt
      shopAdmin {
        _id
        fullName
        email
        phoneNumber
      }
      rootCategory {
        _id
        title
      }
      categories {
        _id
        title
      }
      attributes {
        _id
        name
      }
    }
    getShopsByAdminCount(filters: { ${SHOP_FILTERS} })
  }
`;

const GET_QUEUE_COUNTS = gql`
  query getVerificationQueueCounts {
    waiting: getShopsByAdminCount(filters: { verified: false, isRejected: false })
    rejected: getShopsByAdminCount(filters: { isRejected: true })
    verified: getShopsByAdminCount(filters: { verified: true })
  }
`;

const VERIFY_SHOP = gql`
  mutation verifyShop($id: ID!) {
    verifyShop(id: $id) {
      _id
      verified
    }
  }
`;

/**
 * rejectShopByAdmin has existed on the backend all along — it sets
 * isRejected and stores a message the shop admin's own login flow reads
 * to send the owner back to fix their details. This screen only ever
 * wired up approve, so a super admin could accept a shop but never turn
 * one away or tell the owner why.
 */
const REJECT_SHOP = gql`
  mutation rejectShopByAdmin($id: ID!, $rejectionMessage: String!) {
    rejectShopByAdmin(id: $id, rejectionMessage: $rejectionMessage) {
      _id
      verified
      isRejected
      rejectionMessage
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const QUEUE_OPTIONS = [
  { value: "waiting", label: "Awaiting review" },
  { value: "rejected", label: "Rejected" },
  { value: "verified", label: "Verified" },
];
const QUEUE_LABELS = QUEUE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const QUEUE_FILTERS = {
  waiting: { verified: false, isRejected: false },
  rejected: { isRejected: true },
  verified: { verified: true },
};

const SEARCH_TARGETS = [
  { value: "name", label: "Shop name" },
  { value: "shopAdminName", label: "Owner name" },
];
const SEARCH_LABELS = { name: "Shop name", shopAdminName: "Owner name" };

const SORT_OPTIONS = [
  { value: "oldest", label: "Longest waiting" },
  { value: "newest", label: "Newest first" },
];

const FILTER_DEBOUNCE_MS = 350;

const waitingFor = (value) => {
  if (!value) return "";
  const days = moment().diff(moment(value), "days");
  if (days <= 0) return "Today";
  return `${days} day${days === 1 ? "" : "s"}`;
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const ShopVerifications = () => {
  const [queue, setQueue] = useState("waiting");
  const [searchTarget, setSearchTarget] = useState("name");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("oldest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [detailTarget, setDetailTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionMessage, setRejectionMessage] = useState("");
  const [rejectionError, setRejectionError] = useState("");
  const [working, setWorking] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionResult, setActionResult] = useState("");

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    createdAtSort: 1,
    ...QUEUE_FILTERS.waiting,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      setVariables((v) => ({
        ...v,
        skip: 0,
        name: undefined,
        shopAdminName: undefined,
        verified: undefined,
        isRejected: undefined,
        ...QUEUE_FILTERS[queue],
        ...(term ? { [searchTarget]: term } : {}),
        createdAtSort: sortOption === "oldest" ? 1 : -1,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [queue, searchTarget, searchTerm, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_SHOPS, {
    variables,
    fetchPolicy: "network-only",
  });
  const { data: countsData, refetch: refetchCounts } = useQuery(GET_QUEUE_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [verifyShop] = useMutation(VERIFY_SHOP);
  const [rejectShop] = useMutation(REJECT_SHOP);

  const shops = (data && data.getShopsByAdmin) || [];
  const totalCount = (data && data.getShopsByAdminCount) || 0;
  const counts = countsData || {};

  /* ---------------- actions ---------------- */

  const finish = (message) => {
    setWorking(false);
    setApproveTarget(null);
    setRejectTarget(null);
    setDetailTarget(null);
    setRejectionMessage("");
    setActionResult(message);
    refetch();
    refetchCounts();
  };

  const runApprove = () => {
    if (!approveTarget) return;
    setWorking(true);
    setActionError("");
    setActionResult("");
    verifyShop({ variables: { id: approveTarget._id } })
      .then(() => finish(`${approveTarget.name} is now verified and can start trading.`))
      .catch((err) => {
        setWorking(false);
        setApproveTarget(null);
        setActionError(readableError(err));
      });
  };

  const runReject = () => {
    if (!rejectTarget) return;
    const message = rejectionMessage.trim();
    if (!message) {
      setRejectionError("Tell the owner what needs fixing — they see this message.");
      return;
    }
    setRejectionError("");
    setWorking(true);
    setActionError("");
    setActionResult("");
    rejectShop({ variables: { id: rejectTarget._id, rejectionMessage: message } })
      .then(() => finish(`${rejectTarget.name} was sent back to its owner with your notes.`))
      .catch((err) => {
        setWorking(false);
        setRejectTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superShopsPage superShopVerifyPage">
        <main className="main">
          <PageHeader title="Shop verifications" description="Shops waiting to be let onto the platform." />
          <MetricsSkeleton count={3} />
          <TableSkeleton rows={6} columns={5} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page superShopsPage superShopVerifyPage">
      <main className="main">
        <PageHeader
          title="Shop verifications"
          count={totalCount}
          description="Shops waiting to be let onto the platform. Approving one lets it start trading; rejecting sends it back to its owner with your notes."
        />

        <section className="stats">
          <MetricCard
            icon={<HourglassEmptyIcon />}
            title="Awaiting review"
            value={counts.waiting || 0}
            tone={counts.waiting > 0 ? "warning" : "default"}
          />
          <MetricCard
            icon={<BlockIcon />}
            title="Rejected"
            value={counts.rejected || 0}
            tone={counts.rejected > 0 ? "danger" : "default"}
          />
          <MetricCard
            icon={<VerifiedUserIcon />}
            title="Verified"
            value={counts.verified || 0}
            tone="success"
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder={`Search by ${SEARCH_LABELS[searchTarget].toLowerCase()}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Queue: ${QUEUE_LABELS[queue]}`}
              options={QUEUE_OPTIONS}
              value={queue}
              active={false}
              onChange={(e) => setQueue(e.target.value)}
            />
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={() => setSearchTerm("")}
            onClearAll={() => setSearchTerm("")}
          />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft" />
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              title="Refresh queue"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}
        {actionResult && (
          <div className="dsFormMessage success" role="status">
            {actionResult}
          </div>
        )}

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="superShopsSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Shop</div>
              <div className="tableHeaderCell">Owner</div>
              <div className="tableHeaderCell">Applied</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell tableHeaderAction">Decision</div>
            </div>

            {shops.length === 0 ? (
              <EmptyState
                icon={<VerifiedUserIcon />}
                title={queue === "waiting" ? "Nothing waiting on you" : "Nothing here"}
                message={
                  queue === "waiting"
                    ? "Every shop that has applied has had a decision."
                    : "No shops in this queue match your search."
                }
              />
            ) : (
              shops.map((shop) => {
                const logo = assetUrl(shop.logoUrl);
                return (
                  <div className="row" key={shop._id}>
                    <div className="superShopCell" data-label="Shop">
                      <span className="superShopLogo">
                        {logo ? <img src={logo} alt="" /> : <StorefrontIcon fontSize="small" />}
                      </span>
                      <span className="superShopText">
                        <span className="superShopName">{shop.name}</span>
                        <span className="superShopMeta">
                          {[shop.rootCategory && shop.rootCategory.title, (shop.phoneNumbers || [])[0]]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                    </div>

                    <div className="superShopPerson" data-label="Owner">
                      {shop.shopAdmin ? (
                        <React.Fragment>
                          <span className="superShopPersonName">{shop.shopAdmin.fullName}</span>
                          <span className="superShopPersonMeta">
                            {shop.shopAdmin.phoneNumber || shop.shopAdmin.email}
                          </span>
                        </React.Fragment>
                      ) : (
                        <span className="superShopMuted">No owner</span>
                      )}
                    </div>

                    <span className="updated" data-label="Applied">
                      <strong>{moment(shop.createdAt).format("MMM D, YYYY")}</strong>
                      <span>{waitingFor(shop.createdAt)} ago</span>
                    </span>

                    <span data-label="Status">
                      <StatusBadge
                        status={shop.verified ? "success" : shop.isRejected ? "danger" : "warning"}
                        label={shop.verified ? "Verified" : shop.isRejected ? "Rejected" : "Waiting"}
                      />
                    </span>

                    <div className="superShopActions">
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => setDetailTarget(shop)}
                        title="Review details"
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                      {!shop.verified && (
                        <React.Fragment>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setRejectTarget(shop)}
                            disabled={working}
                          >
                            Reject
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => setApproveTarget(shop)}
                            disabled={working}
                          >
                            Approve
                          </Button>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(detailTarget)}
        onClose={() => setDetailTarget(null)}
        title="Verification details"
        subtitle={detailTarget ? detailTarget.name : ""}
        size="lg"
      >
        {detailTarget && (
          <React.Fragment>
            <ShopVerificationDetails item={detailTarget} />
            {!detailTarget.verified && (
              <div className="superShopFooter">
                <Button variant="secondary" onClick={() => setRejectTarget(detailTarget)}>
                  Reject
                </Button>
                <Button
                  variant="primary"
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  onClick={() => setApproveTarget(detailTarget)}
                >
                  Approve
                </Button>
              </div>
            )}
          </React.Fragment>
        )}
      </DialogShell>

      <DialogShell
        open={Boolean(approveTarget)}
        onClose={() => (working ? null : setApproveTarget(null))}
        title="Approve shop"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setApproveTarget(null), disabled: working },
          { label: "Approve", onClick: runApprove, variant: "primary", loading: working },
        ]}
      >
        <div className="superShopConfirm">
          {approveTarget ? approveTarget.name : "This shop"} will be verified and can start taking
          orders straight away.
        </div>
      </DialogShell>

      <DialogShell
        open={Boolean(rejectTarget)}
        onClose={() => {
          if (working) return;
          setRejectTarget(null);
          setRejectionError("");
        }}
        title="Reject shop"
        subtitle={rejectTarget ? rejectTarget.name : ""}
        actions={[
          {
            label: "Cancel",
            onClick: () => {
              setRejectTarget(null);
              setRejectionError("");
            },
            disabled: working,
          },
          { label: "Reject", onClick: runReject, variant: "danger", loading: working },
        ]}
      >
        <div className="superShopForm">
          <Field
            label="What needs fixing?"
            htmlFor="rejectionMessage"
            error={rejectionError}
            hint="The owner sees this the next time they sign in, and is sent back to edit their shop."
            required
          >
            <textarea
              id="rejectionMessage"
              className="dsInput"
              rows={4}
              value={rejectionMessage}
              onChange={(e) => setRejectionMessage(e.target.value)}
              placeholder="For example: the business licence photo is unreadable."
            />
          </Field>
        </div>
      </DialogShell>
    </div>
  );
};

export default ShopVerifications;
